import {
  SIDER_COLLAPSED_WIDTH,
  SIDER_WIDTH,
} from 'src/components/layout/sidebar'
import { SHELL_GUTTER } from 'src/components/main/shell/geometry'
import type { DesignMode } from 'src/design-mode'

/**
 * WHERE THE INTERCOM LAUNCHER SITS, and why one corner needs two answers.
 *
 * The launcher is bottom-LEFT again (captain's call, 2026-09-11, restoring the
 * corner it had before 2026-08-25). The shipped antd shell takes that
 * placement back EXACTLY as it was: the small edge offset it carried for
 * years. That corner is not reserved for it — the antd sider's nav column runs
 * to the window's bottom edge and scrolls inside itself
 * (`layout/sidebar/components/NavigationMenu/styles.ts`), so a fully scrolled
 * menu can put a nav row under the launcher, as it always could. Restoring the
 * old arrangement is what was asked for, so that is what ships; only the
 * widgets that claim a corner OUTRIGHT (Route Studio's Copilot FloatButton,
 * the toast stack) are elsewhere, and both are bottom-RIGHT.
 *
 * The shadcn shell is the exception, and NOT for the reason a first reading
 * suggests. Its sidebar column (`components/main/shell`) is a viewport-tall
 * stack of rows that ends hard against the window's bottom-left edge: the
 * account row, and the Notifications / Get Help / Settings group directly
 * above it. Lifting the launcher vertically only trades one row for the next
 * one up — the whole left column is rows — so the launcher moves SIDEWAYS
 * instead, to the bottom-left of the CONTENT panel: the column's own gutter,
 * the column's width, and one more gutter as the gap. That is the same
 * spacing rhythm the panel beside it keeps, and it follows the column when it
 * collapses to the rail (captain's call, 2026-09-11).
 *
 * These are Intercom's BOOT SETTINGS (`./useIntercom`), not CSS offsets: they
 * move the messenger panel and its animation origin with the launcher, which
 * CSS on the launcher alone cannot do — and unlike CSS they can be re-sent
 * with `update` when the sidebar is collapsed or the design is switched.
 * `./global.styles.ts` therefore pins NEITHER side any more; it only clears
 * the side Intercom no longer uses.
 */
export const INTERCOM_EDGE_PADDING = 15

/**
 * PRO-929 — how high the launcher sits on a handset OF THE ANTD SHELL.
 *
 * The corner is NOT up for grabs: bottom-left is the captain's call
 * (2026-09-11), twice, and the reasoning above stands. What changes on a
 * phone is only the clearance, because at 360px the launcher sits directly
 * over page content rather than over the comfortable margin a desktop window
 * has. Lifting it clears a row of content without moving it to a corner that
 * Route Studio's Copilot and the toast stack already own.
 *
 * NOT on the shadcn shell, for both of the reasons this module already
 * carries: that shell's narrow-width treatment is deferred, and a vertical
 * lift there would buy nothing anyway — its bottom-left column is rows all
 * the way down, so 72px trades one nav row for the next rather than clearing
 * content, which is exactly why that shell moves the launcher SIDEWAYS. The
 * conjunction lives here rather than at the call site because this is the one
 * function that decides placement and it already knows the shell.
 *
 * SENDING IT IS NOT ENOUGH, AND THAT IS NOT VISIBLE FROM THIS FILE.
 * Intercom DISCARDS both paddings once it decides the browser is mobile
 * (`innerWidth <= 450`, or `<= 900` wide and `<= 450` tall) and pins the
 * launcher 20px off each edge instead — so on a 360px phone, the band this
 * constant exists for, the value below travelled, was stored on
 * `window.intercomSettings`, and did nothing. The lift is therefore also
 * written as CSS, in `GlobalStylesIntercomHandsetLift`
 * (`./global.styles.ts`), whose header carries the evidence out of Intercom's
 * own widget bundle. That rule is scoped to INTERCOM'S own width test
 * (`intercomMobileWidth`, max-width: 450px) rather than to this app's
 * handset band, because from 451px up the setting below is honoured and owns
 * placement on its own; the accepted consequence is that a phone in
 * LANDSCAPE is mobile to Intercom and reached by neither. Keep the two in
 * step: that rule reads THIS constant, so the number still lives here.
 */
export const INTERCOM_HANDSET_VERTICAL_PADDING = 72

export const intercomPadding = (
  designMode: DesignMode,
  sidebarCollapsed: boolean,
  handset = false
) => ({
  vertical_padding:
    handset && designMode !== 'shadcn'
      ? INTERCOM_HANDSET_VERTICAL_PADDING
      : INTERCOM_EDGE_PADDING,
  horizontal_padding:
    designMode === 'shadcn'
      ? SHELL_GUTTER +
        (sidebarCollapsed ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH) +
        SHELL_GUTTER
      : INTERCOM_EDGE_PADDING,
})
